import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReport, STATUS_LABEL } from '../lib/creditProvider';

export default function CreditReport({ user }) {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, status: null, report: null, request: null });

  useEffect(() => {
    if (!user) { navigate('/login?next=/credit/report/' + requestId); return; }
    (async () => {
      const r = await getReport({ requestId });
      setState({ loading: false, ...r });
    })();
  }, [requestId, user, navigate]);

  if (state.loading) return <Page><p style={muted}>Loading report…</p></Page>;
  if (!state.request) return <Page><p style={muted}>Report not found.</p></Page>;

  if (state.request.landlord_id !== user.id) {
    return <Page><h1 style={h1}>Not your report</h1><p style={muted}>This report belongs to a different property management account.</p></Page>;
  }

  const { status, report, request } = state;

  if (status !== 'ready') {
    return (
      <Page>
        <BackBtn onClick={() => navigate('/manager-dashboard')} />
        <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Credit report</div>
        <h1 style={h1}>{request.tenant_name || 'Tenant'}</h1>
        <StatusPill status={status} />
        <p style={{ ...muted, marginTop: 14 }}>
          {status === 'pending_consent' && 'We\'re waiting for the tenant to authorize the report. Share the consent link with them if they haven\'t received it yet.'}
          {status === 'declined' && 'The tenant declined this request. You will not be charged in production billing.'}
          {status === 'expired' && 'This request expired before the tenant consented.'}
          {status === 'consented' && 'Consent received — the bureau is preparing the report. This usually takes seconds.'}
        </p>
        {status === 'pending_consent' && (
          <ConsentLinkBox requestId={requestId} />
        )}
      </Page>
    );
  }

  const r = report;
  const score = r.score;
  const band = scoreBand(score);

  return (
    <Page>
      <BackBtn onClick={() => navigate('/manager-dashboard')} />

      <div style={{ background: '#FFF8E1', border: '1px dashed #C9972A', color: '#7B5E12', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 16 }}>
        ⓘ <b>SAMPLE REPORT</b> — RenterRep is in launch phase. This is placeholder data so the workflow can be reviewed end-to-end. Real bureau integration ships in Phase 2.
      </div>

      <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>Credit report</div>
      <h1 style={h1}>{request.tenant_name || r.identity.nameOnFile}</h1>
      <div style={muted}>
        Pulled {new Date(r.pulledAt).toLocaleDateString()} · {r.scoreModel}
      </div>

      {/* Score hero */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 20, marginBottom: 20 }}>
        <div style={{ ...card, flex: '1 1 220px', textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>Score</div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 56, lineHeight: 1, color: band.color, fontWeight: 400 }}>{score}</div>
          <div style={{ fontSize: 13, color: band.color, fontWeight: 700, marginTop: 4 }}>{band.label}</div>
          <div style={{ fontSize: 11, color: '#6B6358', marginTop: 6 }}>Range 300 – 850</div>
        </div>
        <div style={{ ...card, flex: '2 1 320px', padding: 20 }}>
          <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10 }}>Summary</div>
          <SummaryRow label="Open accounts" value={r.summary.openAccounts} />
          <SummaryRow label="Total balance" value={'$' + r.summary.totalBalance.toLocaleString()} />
          <SummaryRow label="Monthly debt payments" value={'$' + r.summary.monthlyDebt.toLocaleString()} />
          <SummaryRow label="Credit utilization" value={Math.round(r.summary.utilization * 100) + '%'} />
          <SummaryRow label="Oldest account" value={r.summary.oldestAccountYears + ' yrs'} />
          <SummaryRow label="Inquiries (24mo)" value={r.summary.inquiriesLast2Yr} />
        </div>
      </div>

      {/* Payment history */}
      <Section title="Payment history">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Stat label="On time" value={r.paymentHistory.onTime + '%'} good />
          <Stat label="30-day late" value={r.paymentHistory.late30} warn={r.paymentHistory.late30 > 0} />
          <Stat label="60-day late" value={r.paymentHistory.late60} bad={r.paymentHistory.late60 > 0} />
          <Stat label="90+ day late" value={r.paymentHistory.late90Plus} bad={r.paymentHistory.late90Plus > 0} />
        </div>
      </Section>

      {/* Tradelines */}
      <Section title="Open & recent accounts">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6B6358', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={th}>Creditor</th><th style={th}>Type</th><th style={th}>Opened</th>
                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                <th style={{ ...th, textAlign: 'right' }}>Limit</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.tradelines.map((t, i) => (
                <tr key={i} style={{ borderTop: '1px solid rgba(184,137,58,0.18)' }}>
                  <td style={td}>{t.creditor}</td>
                  <td style={td}>{t.type}</td>
                  <td style={td}>{t.opened}</td>
                  <td style={{ ...td, textAlign: 'right' }}>${t.balance.toLocaleString()}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{t.limit ? '$' + t.limit.toLocaleString() : '—'}</td>
                  <td style={td}>{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Public records / evictions / collections */}
      <Section title="Public records & evictions">
        {r.publicRecords.length === 0 && r.evictions.length === 0 && r.collections.length === 0
          ? <div style={{ fontSize: 13, color: '#1B7A4B', fontWeight: 600 }}>✓ No public records, evictions, or collections found.</div>
          : <pre>{JSON.stringify({ publicRecords: r.publicRecords, evictions: r.evictions, collections: r.collections }, null, 2)}</pre>
        }
      </Section>

      <Section title="Identity verification">
        <SummaryRow label="Name on file" value={r.identity.nameOnFile} />
        <SummaryRow label="SSN verified" value={r.identity.ssnVerified ? 'Yes' : 'No'} />
        <SummaryRow label="Address(es) on file" value={r.identity.addressesOnFile.join('; ')} />
      </Section>

      <p style={{ ...muted, fontSize: 11, marginTop: 20 }}>
        Provided to RenterRep under permissible purpose (tenant screening). Treat
        as confidential and FCRA-protected. Do not share, post, or redistribute.
      </p>
    </Page>
  );
}

function ConsentLinkBox({ requestId }) {
  const url = window.location.origin + '/credit/consent/' + requestId;
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginTop: 18, background: '#F5EFE3', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0F2C4E', marginBottom: 6 }}>Tenant consent link</div>
      <div style={{ fontSize: 12, color: '#6B6358', marginBottom: 8 }}>Send this to your tenant. They'll need to sign in as themselves to authorize.</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code style={{ flex: 1, background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.18)', padding: '8px 10px', borderRadius: 6, fontSize: 12, overflow: 'auto', whiteSpace: 'nowrap' }}>{url}</code>
        <button
          onClick={() => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
        >
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_consent: { bg: '#FFF8E1', fg: '#7B5E12', border: '#C9972A' },
    consented:       { bg: '#E6F0FB', fg: '#0F2C4E', border: '#0F2C4E' },
    ready:           { bg: '#E8F5EE', fg: '#1B7A4B', border: '#1B7A4B' },
    declined:        { bg: '#FDEDEB', fg: '#C0392B', border: '#C0392B' },
    expired:         { bg: '#F5EFE3', fg: '#6B6358', border: '#6B6358' },
  };
  const c = map[status] || map.pending_consent;
  return (
    <span style={{ display: 'inline-block', background: c.bg, color: c.fg, border: '1px solid ' + c.border, padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function BackBtn({ onClick }) {
  return <button onClick={onClick} style={{ background: 'none', border: 'none', color: '#6B6358', fontSize: 13, cursor: 'pointer', marginBottom: 12, padding: '6px 0' }}>← Back to dashboard</button>;
}

function Section({ title, children }) {
  return (
    <div style={{ ...card, padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}
function SummaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(184,137,58,0.12)', fontSize: 13 }}>
      <span style={{ color: '#6B6358' }}>{label}</span>
      <span style={{ color: '#0F2C4E', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
function Stat({ label, value, good, warn, bad }) {
  const color = bad ? '#C0392B' : warn ? '#C9972A' : good ? '#1B7A4B' : '#0F2C4E';
  return (
    <div style={{ flex: '1 1 120px', background: '#F5EFE3', border: '1px solid rgba(184,137,58,0.18)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#6B6358', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "'DM Serif Display', serif" }}>{value}</div>
    </div>
  );
}

function scoreBand(s) {
  if (s >= 740) return { label: 'Excellent', color: '#1B7A4B' };
  if (s >= 670) return { label: 'Good',      color: '#1B7A4B' };
  if (s >= 580) return { label: 'Fair',      color: '#C9972A' };
  return            { label: 'Poor',      color: '#C0392B' };
}

function Page({ children }) {
  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div className="container" style={{ paddingTop: 20, paddingBottom: 48, maxWidth: 920 }}>{children}</div>
    </div>
  );
}

const card = { background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 16, boxShadow: '0 2px 8px rgba(120,90,30,0.06)' };
const h1 = { fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: '#0F2C4E', marginBottom: 4, lineHeight: 1.2 };
const muted = { fontSize: 13, color: '#6B6358', lineHeight: 1.6 };
const th = { padding: '8px 10px', fontWeight: 700 };
const td = { padding: '8px 10px', color: '#2C2C2C' };
